import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Star, Heart, Eye, Filter, Sparkles, Leaf, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useContent, PRODUCT_CATEGORIES, normalizeProductCategory } from '../context/ContentContext';
import apiService from '../services/api.service';

const WellnessAccessories = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { products: localProducts } = useContent();
  
  const [backendProducts, setBackendProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load products from backend
  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.adminProducts.getAll();
        const payload = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        if (payload.length > 0) {
          setBackendProducts(payload);
        }
      } catch (error) {
        console.error('[WellnessAccessories] Failed to load products:', error);
        // Fallback to local products if backend fails
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Use backend products if available, otherwise fall back to local products
  const products = backendProducts.length > 0 ? backendProducts : localProducts;

  const normalizedProducts = useMemo(() => (
    products.map((item) => ({
      ...item,
      id: item.id ?? item._id ?? item.productId,
      name: item.name || item.title || 'Untitled Product',
      image: item.image || item.imageUrl || item.src,
      category: normalizeProductCategory(item.category),
      price: item.price ?? item.cost ?? item.amount ?? item.displayPrice,
      rating: typeof item.rating === 'number' ? item.rating : 4.8,
      reviews: typeof item.reviews === 'number' ? item.reviews : 0,
      inStock: typeof item.inStock === 'boolean' ? item.inStock : true
    }))
  ), [products]);

  const categories = useMemo(() => {
    const existing = new Set(PRODUCT_CATEGORIES.map((item) => item.value));
    const extraCategories = Array.from(
      new Set(normalizedProducts.map((item) => item.category).filter(Boolean))
    )
      .filter((value) => !existing.has(value))
      .map((value) => ({
        value,
        label: value
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ')
      }));

    return [...PRODUCT_CATEGORIES, ...extraCategories];
  }, [normalizedProducts]);

  const filteredAccessories = selectedCategory === 'all' 
    ? normalizedProducts 
    : normalizedProducts.filter(item => item.category === selectedCategory);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${
          i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`} 
      />
    ));
  };

  const handleAddToCart = (item) => {
    if (!user) {
      navigate('/login');
      return;
    }
    addToCart(item);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center"
        style={{
          backgroundImage: 'linear-gradient(135deg, hsl(var(--spiritual) / 0.9) 0%, hsl(var(--compassion) / 0.8) 100%)'
        }}
        data-testid="wellness-accessories-hero"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mb-6 fade-in">
            Wellness 
            <span className="bg-gradient-to-r from-compassionScale-100 to-sacredScale-100 bg-clip-text text-transparent">
              Accessories
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-compassionScale-100 max-w-3xl mx-auto leading-relaxed slide-up">
            Sacred tools and accessories to support your spiritual journey and enhance 
            your healing practices at home.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="section-padding-sm bg-gradient-to-b from-compassionScale-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
            <div className="mb-4 sm:mb-0">
              <h2 className="font-heading font-bold text-2xl text-spiritual">Our Collection</h2>
              <p className="text-compassion-foreground">Curated accessories for your spiritual practice</p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-spiritual" />
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAccessories.map((item) => (
              <Card 
                key={item.id} 
                className="card group hover:scale-105 transition-all duration-300 overflow-hidden"
                data-testid={`accessory-${item.id}`}
              >
                <CardContent className="p-0">
                  <div className="relative">
                    <img 
                      src={item.image} 
                      alt={item.name} 
                      className={`w-full h-64 group-hover:scale-110 transition-transform duration-300 ${
                        item.name === 'Aura Spray' || item.name === 'Energy Mist'
                          ? 'object-contain bg-white p-4' 
                          : 'object-cover'
                      }`}
                    />
                    <div className="absolute top-4 right-4">
                      <button className="w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors duration-300">
                        <Heart className="w-5 h-5 text-spiritual" />
                      </button>
                    </div>
                    {!item.inStock && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-500 text-white px-4 py-2 rounded-full font-semibold">
                          Out of Stock
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="mb-3">
                      <h3 className="font-heading font-semibold text-xl text-spiritual mb-2 group-hover:text-spiritual/90 transition-colors">
                        {item.name}
                      </h3>
                      <p className="text-compassion-foreground text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center">
                          {renderStars(item.rating)}
                        </div>
                        <span className="text-sm text-compassion-foreground">
                          ({item.reviews} reviews)
                        </span>
                      </div>
                      <span className="font-bold text-xl text-spiritual">
                        {item.price}
                      </span>
                    </div>
                    
                    <div className="flex space-x-3">
                      <Button 
                        className={`flex-1 font-semibold transition-all duration-300 ${
                          item.inStock 
                            ? user
                              ? 'bg-spiritual hover:bg-spiritual/90 text-white cursor-pointer'
                              : 'bg-gray-400 text-white cursor-not-allowed opacity-75'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        disabled={!item.inStock || !user}
                        onClick={() => handleAddToCart(item)}
                        data-testid={`add-to-cart-${item.id}`}
                        title={!user && item.inStock ? 'Please login to add to cart' : ''}
                      >
                        <ShoppingBag className="mr-2 w-4 h-4" />
                        {!item.inStock ? 'Out of Stock' : user ? 'Add to Cart' : 'Login to Add'}
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="px-4 border-spiritual text-spiritual hover:bg-compassionScale-50"
                        data-testid={`quick-view-${item.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-4xl text-spiritual mb-6">
              Shop by Category
            </h2>
            <p className="text-lg text-compassion-foreground max-w-2xl mx-auto">
              Explore our carefully curated collections designed to support different aspects 
              of your spiritual and wellness journey.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {categories.slice(1, 4).map((category, index) => (
              <Card 
                key={category.value}
                className="card group cursor-pointer hover:scale-105 transition-all duration-300"
                onClick={() => setSelectedCategory(category.value)}
                data-testid={`category-${category.value}`}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-compassionScale-100 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:bg-compassionScale-200 transition-colors duration-300">
                    {index === 0 && <Sparkles className="w-8 h-8 text-spiritual" />}
                    {index === 1 && <Heart className="w-8 h-8 text-spiritual" />}
                    {index === 2 && <Leaf className="w-8 h-8 text-spiritual" />}
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-spiritual mb-3">
                    {category.label}
                  </h3>
                  <p className="text-compassion-foreground mb-4">
                    {index === 0 && "Healing crystals and gemstones for energy work"}
                    {index === 1 && "Tools for deeper meditation and mindfulness"}
                    {index === 2 && "Essential oils and diffusers for sacred spaces"}
                  </p>
                  <Button 
                    variant="outline" 
                    className="border-spiritual text-spiritual hover:bg-compassionScale-50"
                  >
                    Explore Collection
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section-padding bg-gradient-to-r from-spiritual via-compassion to-spiritual">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl text-white mb-6">
            Stay Connected
          </h2>
          <p className="text-xl text-compassionScale-100 mb-8 leading-relaxed">
            Get updates on new arrivals, spiritual practices, and exclusive offers 
            for our wellness community.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-spiritual hover:bg-compassionScale-50 px-8 py-4 text-lg font-semibold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              Join Our Community
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WellnessAccessories;
